// ---------------------------------------------------------------------------
// helpers/api_helper.js
//
// Single-client API helper. Used by:
//   - Routes/AuthProtected.js          (imports setAuthorization)
//   - Components/Hooks/UserHooks.js    (imports getLoggedinUser)
//   - helpers/fakebackend_helper.js    (imports APIClient — used by auth thunks)
//
// Previously this file mutated `axios.defaults.baseURL` from `src/config.js`,
// which conflicted with `src/api.js` and caused two different base URLs to
// coexist in the app. It also read the JWT once at module load, so any
// `APIClient` call after a fresh login carried no Authorization header.
//
// The rewrite:
//   1. Routes every method through the named `api` axios instance from
//      `src/api.js`, so JWT / X-EmpId / X-Dept / X-Role / multipart
//      handling are applied freshly on every request.
//   2. Preserves the public API surface: APIClient.get / create / update /
//      put / delete, plus setAuthorization and getLoggedinUser exports.
//   3. Preserves the "return response.data when truthy, else full response"
//      semantic that the previous global response interceptor implemented,
//      so callers that destructure top-level fields keep working.
//
// What we deliberately do NOT do here:
//   - Touch `src/config.js` (legacy module is left in place for other callers).
//   - Mutate axios global defaults (the cause of the dual-base-URL bug).
//   - Try to be clever with the error map (see mapError note below).
// ---------------------------------------------------------------------------
import api from "../api";

/**
 * Historical no-op kept for backward compatibility.
 *
 * Token was previously stamped onto axios global defaults here, once at
 * module load. With the single-client design, `src/api.js` reads the JWT
 * from sessionStorage on EVERY request via interceptor, so the token is
 * always fresh and this function has nothing to do. The export is kept
 * so existing callers (Routes/AuthProtected.js) continue to compile.
 */
const setAuthorization = (_token) => {
  // Intentionally empty. See api.js request interceptor.
};

/** Reads the auth user from sessionStorage. Returns null when absent. */
const getLoggedinUser = () => {
  const user = sessionStorage.getItem("authUser");
  if (!user) return null;
  try {
    return JSON.parse(user);
  } catch {
    return null;
  }
};

/**
 * Unwrap `response.data` when present and truthy, mirroring the original
 * global-axios response interceptor. Callers like fakebackend_helper
 * (`api.create(url, data) => data`) depend on this shape.
 */
function unwrap(response) {
  return response && response.data ? response.data : response;
}

/**
 * Preserved error-message map.
 *
 * Note: The original used `error.status` (not `error.response?.status`),
 * which is never set on a standard Axios error, so the switch always
 * fell through to `default` and callers received `error.message`. We
 * preserve that exact behavior here so we do not change what existing
 * callers see at the catch site. A correctness pass on error mapping
 * is deferred to a later phase.
 */
function mapError(error) {
  let message;
  switch (error && error.status) {
    case 500:
      message = "Internal Server Error";
      break;
    case 401:
      message = "Invalid credentials";
      break;
    case 404:
      message = "Sorry! the data you are looking for could not be found";
      break;
    default:
      message = (error && error.message) || error;
  }
  return Promise.reject(message);
}

class APIClient {
  /**
   * GET — preserves the original "build manual query string from a params
   * object" behavior. We still pass `params` as the axios config so that
   * caller objects of shape `{ headers, signal, ... }` continue to work.
   */
  get = (url, params) => {
    let request;
    if (params) {
      const paramKeys = Object.keys(params).map((k) => `${k}=${params[k]}`);
      const qs = paramKeys.length ? paramKeys.join("&") : "";
      request = api.get(`${url}?${qs}`, params);
    } else {
      request = api.get(`${url}`);
    }
    return request.then(unwrap).catch(mapError);
  };

  /** POST */
  create = (url, data) =>
    api.post(url, data).then(unwrap).catch(mapError);

  /** PATCH (kept as `update` for caller compat) */
  update = (url, data) =>
    api.patch(url, data).then(unwrap).catch(mapError);

  /** PUT */
  put = (url, data) =>
    api.put(url, data).then(unwrap).catch(mapError);

  /** DELETE — accepts an optional axios config (`{ headers, data }` etc.) */
  delete = (url, config) =>
    api.delete(url, { ...config }).then(unwrap).catch(mapError);
}

export { APIClient, setAuthorization, getLoggedinUser };
